import { getToken, clearToken } from '../utils/authToken';
import { useState, useEffect, useRef } from 'react';
import { Crown, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const PremiumCrownBadge = ({ language, subscriptionData }) => {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const wrapperRef = useRef(null);
  
  const currentPeriod = subscriptionData?.period || 'monthly';
  const isCreator = subscriptionData?.is_creator === true;
  
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    requestAnimationFrame(() => {
      document.addEventListener('pointerdown', close);
    });
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  
  const handleSwitchPlan = async (period) => {
    if (period === currentPeriod || isCreator) return;
    const token = getToken();
    if (!token) return;
    setSwitching(period);
    try {
      const { API_URL } = await import('../config/api');
      const resp = await axios.post(
        `${API_URL}/subscriptions/checkout`,
        { period, origin_url: window.location.origin },
        {}
      );
      if (resp.data.checkout_url) {
        const win = window.open(resp.data.checkout_url, '_blank');
        if (!win) window.location.href = resp.data.checkout_url;
      }
      setOpen(false);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : (language === 'es' ? 'Error al cambiar plan' : 'Error switching plan'));
    } finally {
      setSwitching(null);
    }
  };
  
  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="p-1.5 rounded-lg transition-all text-pink-400 hover:bg-pink-500/20 hover:text-pink-300"
        title={isCreator ? (language === 'es' ? 'Premium Permanente' : 'Permanent Premium') : (language === 'es' ? 'Premium - Cambiar plan' : 'Premium - Switch plan')}
        data-testid="premium-crown-badge"
      >
        <Crown className="w-4 h-4" />
      </button>
      
      {open && (
        <div 
          style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 99999, width: 192 }}
          className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl overflow-hidden"
          data-testid="premium-plan-menu"
        >
          <div className="px-3 py-2 border-b border-slate-700">
            <p className="text-xs text-pink-400 font-medium flex items-center gap-1">
              <Crown className="w-3 h-3" />
              {language === 'es' ? 'Plan Premium' : 'Premium Plan'}
            </p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              {isCreator
                ? (language === 'es' ? 'Premium Permanente' : 'Permanent Premium')
                : currentPeriod === 'yearly' 
                  ? (language === 'es' ? 'Activo: Anual $86' : 'Active: Yearly $86')
                  : (language === 'es' ? 'Activo: Mensual $9' : 'Active: Monthly $9')
              }
            </p>
          </div>
          {!isCreator && (
            <>
              <button
                onClick={() => handleSwitchPlan('monthly')}
                disabled={currentPeriod === 'monthly' || switching !== null}
                className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between transition-colors ${
                  currentPeriod === 'monthly' 
                    ? 'text-slate-500 cursor-default bg-slate-700/30' 
                    : 'text-slate-200 hover:bg-slate-700'
                }`}
                data-testid="switch-to-monthly"
              >
                <span>{language === 'es' ? 'Mensual' : 'Monthly'} — $9/mes</span>
                {currentPeriod === 'monthly' && <Check className="w-3 h-3 text-emerald-400" />}
                {switching === 'monthly' && <Loader2 className="w-3 h-3 animate-spin" />}
              </button>
              <button
                onClick={() => handleSwitchPlan('yearly')}
                disabled={currentPeriod === 'yearly' || switching !== null}
                className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between transition-colors ${
                  currentPeriod === 'yearly' 
                    ? 'text-slate-500 cursor-default bg-slate-700/30' 
                    : 'text-slate-200 hover:bg-slate-700'
                }`}
                data-testid="switch-to-yearly"
              >
                <span>{language === 'es' ? 'Anual' : 'Yearly'} — $86/año</span>
                {currentPeriod === 'yearly' && <Check className="w-3 h-3 text-emerald-400" />}
                {switching === 'yearly' && <Loader2 className="w-3 h-3 animate-spin" />}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PremiumCrownBadge;
